import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, Dialog, InputBase, LinearProgress, List, ListItemButton, ListItemText, ListSubheader, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { filterCommands, flattenSearchResults, matchRange, rankItems, readRecents, rememberRecent, shouldSearch, toCommands, toKnowledgeCommands, withRecentsSection, groupBySection, inDisplayOrder } from './commands';
import { isLivyAdmin } from './access';
import { parseJwt } from 'src/core/services/jwt';
import { livyConfig, livyFetch } from 'src/lib/livyTransport';
import { freshToken } from 'src/lib/livyApi';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { LivyTheme } from './LivyTheme';

/** Event the header search field dispatches; the palette owns its open state, not the header. */
export const PALETTE_EVENT = 'livy:palette';

/**
 * Cmd-K / Ctrl-K anywhere in the app, plus the header search field.
 *
 * `enabled` is the same gate the launcher applies. Binding regardless meant preventDefault ran for
 * everyone, so Cmd-K stopped reaching the browser for every user who has no palette to open. The
 * launcher mounts app-wide and hooks run before its early return, so dropping the parameter does
 * not just widen the binding, it swallows the shortcut for every non-admin.
 */
export function useCommandPalette() {
  let enabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    const onRequest = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(PALETTE_EVENT, onRequest);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(PALETTE_EVENT, onRequest);
    };
  }, [enabled]);
  return {
    open,
    setOpen
  };
}
const SCOPE_KEY = 'livy_search_scope';

/**
 * Which companies a search covers. Staff only, because widening it reads past the tenant fence.
 *
 * Remembered per company, so switching the company you are impersonating starts you back inside
 * its fence rather than silently leaving you reading the whole platform. Anything unreadable, or
 * remembered against another company, falls back to the narrow answer and never the wide one.
 */
export function readScope(companyId) {
  try {
    var _localStorage$getItem;
    const saved = JSON.parse((_localStorage$getItem = localStorage.getItem(SCOPE_KEY)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : 'null');
    return (saved === null || saved === void 0 ? void 0 : saved.scope) === 'platform' && (saved === null || saved === void 0 ? void 0 : saved.companyId) === companyId ? 'platform' : 'company';
  } catch (_unused) {
    return 'company';
  }
}
function rememberScope(companyId, scope) {
  try {
    localStorage.setItem(SCOPE_KEY, JSON.stringify({
      companyId,
      scope
    }));
  } catch (_unused2) {
    /* a browser that refuses storage still gets a working toggle, just not a remembered one */
  }
}

/**
 * The company the caller is acting in, read from the token rather than from a settings key.
 *
 * It is the same claim the proxy fences on, so the palette's idea of which company it is in can
 * never drift from the server's - and USER_SETTINGS_KEYS.companyId is not written by the
 * impersonation flow at all, so keying on it meant the scope below never reset.
 */
function currentCompanyId() {
  var _claims$customCompan;
  const token = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);
  const claims = token ? parseJwt(token) : null;
  return (_claims$customCompan = claims === null || claims === void 0 ? void 0 : claims['custom:companyid']) !== null && _claims$customCompan !== void 0 ? _claims$customCompan : null;
}

/** Never past the end, never below zero — an out-of-range cursor makes Enter do nothing. */
const clampCursor = (next, length) => Math.max(0, Math.min(next, length - 1));

/** Emphasises the typed substring, so a long list shows why each row is in it. */
const Highlighted = _ref => {
  let label = _ref.label,
    query = _ref.query;
  const range = matchRange(label, query);
  if (!range) return /*#__PURE__*/React.createElement(React.Fragment, null, label);
  const _range = _slicedToArray(range, 2),
    from = _range[0],
    to = _range[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, label.slice(0, from), /*#__PURE__*/React.createElement(Box, {
    component: "mark",
    sx: {
      bgcolor: 'transparent',
      color: 'inherit',
      fontWeight: 700
    }
  }, label.slice(from, to)), label.slice(to));
};
const LivyCommandPaletteBody = observer(_ref2 => {
  var _store$account$featur, _store$account$featur2, _store$company$compan, _store$company$compan2, _store$company$compan3, _store$account$featur3;
  let open = _ref2.open,
    onClose = _ref2.onClose;
  const navigate = useNavigate();
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    query = _useState4[0],
    setQuery = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    cursor = _useState6[0],
    setCursor = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    hits = _useState8[0],
    setHits = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    articles = _useState0[0],
    setArticles = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    searching = _useState10[0],
    setSearching = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    failed = _useState12[0],
    setFailed = _useState12[1];
  const _useState13 = useState(0),
    _useState14 = _slicedToArray(_useState13, 2),
    attempt = _useState14[0],
    setAttempt = _useState14[1];
  // Reactive, not read inside the memo: the component stays mounted while the dialog is closed, so
  // a selection made on an empty query would otherwise not show up the next time it is opened.
  const _useState15 = useState(readRecents),
    _useState16 = _slicedToArray(_useState15, 2),
    recents = _useState16[0],
    setRecents = _useState16[1];
  const listRef = useRef(null);
  const role = localStorage.getItem(USER_SETTINGS_KEYS.userRole);

  // Presentation only. The proxy refuses platform scope to anyone whose verified token is not
  // staff, so hiding the control is about not offering a door that leads nowhere.
  const canWiden = isLivyAdmin(role);
  const companyId = currentCompanyId();
  const companyName = localStorage.getItem(USER_SETTINGS_KEYS.companyName) || 'This company';
  const _useState17 = useState(() => canWiden ? readScope(companyId) : 'company'),
    _useState18 = _slicedToArray(_useState17, 2),
    scope = _useState18[0],
    setScope = _useState18[1];
  // What the server said it actually did, which is not always what was asked: a new bundle against
  // an older Lambda gets company scope, and labelling that "all companies" would be a lie.
  const _useState19 = useState(scope),
    _useState20 = _slicedToArray(_useState19, 2),
    appliedScope = _useState20[0],
    setAppliedScope = _useState20[1];
  const chooseScope = useCallback(next => {
    setScope(next);
    rememberScope(companyId, next);
  }, [companyId]);

  // The palette body stays mounted between openings, so the initialiser above runs once for the
  // life of the session. Without this, choosing All companies and then switching the company you
  // are impersonating left the next search still reading every tenant.
  useEffect(() => {
    setScope(canWiden ? readScope(companyId) : 'company');
  }, [companyId, canWiden]);

  // The same flags the sidebar checks, so the palette cannot open a destination it hides.
  const store = useStore();
  const newPaymentsAndSubscriptions = Boolean((_store$account$featur = store.account.featureFlagsStore) === null || _store$account$featur === void 0 ? void 0 : _store$account$featur.getIsNewPaymentsAndSubscriptions);
  const stripeInvoicing = Boolean((_store$account$featur2 = store.account.featureFlagsStore) === null || _store$account$featur2 === void 0 ? void 0 : _store$account$featur2.getIsStripeInvoicingApiEnabled);
  const canAddProperty = !((_store$company$compan = store.company.companyFeatureStore) !== null && _store$company$compan !== void 0 && _store$company$compan.isAppfolio);
  const notDemoMode = localStorage.getItem(AUTH_TOKEN_KEYS.demoPropertyMode) !== 'Demo';
  const canUseFeeStructure = !((_store$company$compan2 = store.company.companyFeatureStore) !== null && _store$company$compan2 !== void 0 && _store$company$compan2.disableFeeAssessmentMethod) && !((_store$company$compan3 = store.company.companyFeatureStore) !== null && _store$company$compan3 !== void 0 && _store$company$compan3.isAppfolio);
  const billsDashboard = Boolean((_store$account$featur3 = store.account.featureFlagsStore) === null || _store$account$featur3 === void 0 ? void 0 : _store$account$featur3.getIsBillsDashboardEnabled);
  const commands = useMemo(() => filterCommands(query, role, {
    newPaymentsAndSubscriptions,
    stripeInvoicing,
    canAddProperty,
    notDemoMode,
    canUseFeeStructure,
    billsDashboard
  }), [query, role, newPaymentsAndSubscriptions, stripeInvoicing, canAddProperty, notDemoMode, canUseFeeStructure, billsDashboard]);

  // Ranked, then grouped, then flattened: the keyboard walks exactly what is on screen.
  const items = useMemo(() => {
    const ranked = rankItems([...commands, ...toCommands(hits), ...toKnowledgeCommands(articles)], query);
    return inDisplayOrder(query.trim() ? ranked : withRecentsSection(ranked, recents));
  }, [commands, hits, articles, query, recents]);

  // Sections render as headings while the flat order still drives the keyboard, so ArrowDown never
  // jumps between panes and Enter always opens the highlighted row.
  const sections = useMemo(() => groupBySection(items), [items]);
  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Another surface may have navigated since this palette was last opened, and the body stays
  // mounted while the dialog is closed — so the cursor has to start over too, or reopening
  // highlights whatever now sits at the old index.
  useEffect(() => {
    if (!open) return;
    setRecents(readRecents());
    setCursor(0);
  }, [open]);

  // The list can also shrink without the query changing — a retry clears results, a search
  // returns fewer rows — which would leave the cursor past the end and Enter doing nothing.
  useEffect(() => {
    setCursor(c => clampCursor(c, items.length));
  }, [items.length]);

  // Debounced so a fast typist issues one query, and aborted so a stale response cannot overwrite
  // the results for what is now on screen.
  useEffect(() => {
    const config = livyConfig();
    if (!open || !config || !shouldSearch(query)) {
      setHits([]);
      setArticles([]);
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    const controller = new AbortController();
    // Cleared as the new search starts, not when it returns: otherwise Enter during the debounce
    // opens the previous query's first result, which is a different property or resident.
    setHits([]);
    setArticles([]);
    setSearching(true);
    setFailed(false);

    // freshToken, not the raw store: livyFetch signs the request here and never passes through the
    // app's Axios refresh interceptor, so after an idle session every search 401s, retry included.
    const get = async path => {
      const token = await freshToken();
      if (!token) throw new Error("".concat(path, " -> no session"));
      const res = await livyFetch(config, token, {
        method: 'GET',
        path,
        signal: controller.signal
      });
      if (!res.ok) throw new Error("".concat(path, " -> ").concat(res.status));
      return res.json();
    };
    const timer = setTimeout(() => {
      // A search that fails silently is indistinguishable from a search that found nothing, which
      // is exactly how a broken palette looks like an empty one. Failure gets its own state.
      const onError = e => {
        if (controller.signal.aborted || (e === null || e === void 0 ? void 0 : e.name) === 'AbortError') return;
        console.error('[Livy] search failed', e);
        setFailed(true);
      };

      // Every setter is guarded: abort() cannot stop a response that has already resolved, so
      // without this an earlier query's results land on top of what is now on screen.
      const entities = get("/search?q=".concat(encodeURIComponent(query), "&scope=").concat(scope)).then(data => {
        if (controller.signal.aborted) return;
        setAppliedScope(data.scope === 'platform' ? 'platform' : 'company');
        const _flattenSearchResults = flattenSearchResults(data.results),
          rows = _flattenSearchResults.hits,
          dropped = _flattenSearchResults.dropped;
        if (dropped) console.warn("[Livy] ".concat(dropped, " search rows had no destination and were not shown"));
        setHits(rows);
      }).catch(onError);

      // Matched server side over title and body, so an answer inside an article is findable and
      // the whole corpus no longer crosses the wire on every keystroke.
      const knowledge = get("/kb/search?q=".concat(encodeURIComponent(query))).then(data => {
        var _data$articles;
        if (controller.signal.aborted) return;
        setArticles((_data$articles = data.articles) !== null && _data$articles !== void 0 ? _data$articles : []);
      }).catch(onError);
      void Promise.all([entities, knowledge]).finally(() => {
        if (!controller.signal.aborted) setSearching(false);
      });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, attempt, scope]);

  // Keep the highlighted row on screen; without this the cursor walked off the bottom of the list.
  useEffect(() => {
    var _listRef$current;
    (_listRef$current = listRef.current) === null || _listRef$current === void 0 || (_listRef$current = _listRef$current.querySelector('[aria-selected="true"]')) === null || _listRef$current === void 0 || _listRef$current.scrollIntoView({
      block: 'nearest'
    });
  }, [cursor, items.length]);
  const run = useCallback(cmd => {
    if (!(cmd !== null && cmd !== void 0 && cmd.path)) return;
    rememberRecent(cmd.id);
    setRecents(readRecents());
    onClose();
    setQuery('');
    navigate(cmd.path);
  }, [navigate, onClose]);
  const activeId = items[cursor] ? "livy-option-".concat(cursor) : undefined;
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm",
    PaperProps: {
      sx: {
        position: 'fixed',
        top: 80,
        m: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '12px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(InputBase, {
    autoFocus: true,
    fullWidth: true,
    value: query,
    placeholder: "Search or jump to\u2026",
    onChange: e => setQuery(e.target.value),
    onKeyDown: e => {
      // Flipping scope without leaving the input: the point of the palette is that a hand
      // never has to leave the keyboard.
      if (canWiden && e.altKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        chooseScope(scope === 'platform' ? 'company' : 'platform');
        return;
      }
      // One unified list, so keyboard focus never jumps between panes.
      // Both bounds, both ways: ArrowDown on an empty list gave Math.min(1, -1) = -1, and a
      // negative cursor stuck until some unrelated state change happened to clamp it.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(c => clampCursor(c + 1, items.length));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(c => clampCursor(c - 1, items.length));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        run(items[cursor]);
      }
    },
    inputProps: {
      'aria-label': 'Command palette',
      role: 'combobox',
      // The listbox is always rendered while the dialog is open — it carries the searching and
      // empty states too — so the popup is expanded whether or not it currently holds an option.
      'aria-expanded': true,
      'aria-controls': 'livy-palette-list',
      'aria-activedescendant': activeId,
      'aria-autocomplete': 'list'
    }
  })), canWiden && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    alignItems: "center",
    sx: {
      px: '16px',
      py: '8px',
      borderBottom: 1,
      borderColor: 'divider',
      flexWrap: 'wrap',
      rowGap: '6px'
    }
  }, [['company', companyName], ['platform', 'All companies']].map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      value = _ref4[0],
      label = _ref4[1];
    return /*#__PURE__*/React.createElement(Chip, {
      key: value,
      size: "small",
      label: label,
      onClick: () => chooseScope(value),
      variant: scope === value ? 'filled' : 'outlined',
      color: scope === value ? 'primary' : 'default',
      sx: {
        maxWidth: 240
      }
    });
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      ml: 'auto'
    }
  }, scope === 'platform' ? 'alt+a narrows' : 'alt+a widens')), scope === 'platform' && appliedScope === 'company' && shouldSearch(query) && !searching && /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "outlined",
    sx: {
      borderRadius: 0,
      borderLeft: 0,
      borderRight: 0
    }
  }, "This search covered ", companyName, " only."), /*#__PURE__*/React.createElement(LinearProgress, {
    sx: {
      height: 2,
      opacity: searching ? 1 : 0,
      transition: 'opacity .15s'
    },
    "aria-label": searching ? 'Searching' : undefined
  }), failed && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      borderRadius: 0,
      borderLeft: 0,
      borderRight: 0
    },
    action: /*#__PURE__*/React.createElement(Typography, {
      component: "button",
      onClick: () => setAttempt(a => a + 1),
      sx: {
        background: 'none',
        border: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        textDecoration: 'underline'
      }
    }, "Retry")
  }, "Search is unavailable right now. Navigation still works."), /*#__PURE__*/React.createElement(List, {
    dense: true,
    ref: listRef,
    id: "livy-palette-list",
    role: "listbox",
    "aria-label": "Search results",
    sx: {
      maxHeight: 360,
      overflowY: 'auto',
      py: 0
    }
  }, sections.map(section => /*#__PURE__*/React.createElement("li", {
    key: section.label
  }, /*#__PURE__*/React.createElement(ListSubheader, {
    component: "div",
    disableSticky: true,
    sx: {
      lineHeight: '28px',
      fontSize: 11,
      letterSpacing: '.08em',
      textTransform: 'uppercase'
    }
  }, section.label, " \xB7 ", section.items.length), /*#__PURE__*/React.createElement("ul", {
    style: {
      padding: 0,
      margin: 0,
      listStyle: 'none'
    }
  }, section.items.map(_ref5 => {
    let cmd = _ref5.cmd,
      index = _ref5.index;
    return /*#__PURE__*/React.createElement(ListItemButton, {
      component: "li",
      key: cmd.id,
      id: "livy-option-".concat(index),
      role: "option",
      "aria-selected": index === cursor,
      selected: index === cursor,
      onClick: () => run(cmd)
    }, /*#__PURE__*/React.createElement(ListItemText, {
      primary: /*#__PURE__*/React.createElement(Highlighted, {
        label: cmd.label,
        query: query
      }),
      secondary: cmd.keywords ? "".concat(cmd.section, " \xB7 ").concat(cmd.keywords) : cmd.section
    }));
  })))), items.length === 0 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, searching ? 'Searching…' : failed ? 'Navigation and actions are still listed here.' : !shouldSearch(query) ? 'Type to search.' : appliedScope === 'platform' ? 'No company, property or resident matched. Units are only searched inside a company.' : 'No matches.'))), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    sx: {
      px: '16px',
      py: '8px',
      borderTop: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u2191\u2193 navigate"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u21B5 select"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "esc close")));
});

/** Ripples off inside Livy: see LivyTheme. */
export function LivyCommandPalette(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyCommandPaletteBody, props));
}